import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import * as Sentry from '@sentry/react';
import { Offline as OfflineIntegration } from '@sentry/integrations';
import { compose } from 'lodash/fp';
import { useAppDispatch, useAppSelector } from '../Redux/store';
import {
    attemptSignIn,
    getDeviceId,
    checkUserExists,
    getUserExists,
    fetchUser,
    fetchStores,
    fetchPurchases,
    getUser,
    getStores,
    getScreenByDeviceId,
    setScreenStatus,
    getActiveScreen,
    ScreenStatus,
    fetchStoreMenu,
    getActiveStore,
} from '../Redux';
import { auth } from '../Lib/firebase';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import api from '../API';
import { FirebaseUser } from '../Types';
import { removeAxiosHeaders, setAuthHeader } from '../API/axios';
import { useInterval } from '../Hooks';
import { getDeviceInfo, getDeviceToken } from '../Utils/helpers';

if (process.env.REACT_APP_ENV === 'production') {
    Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        integrations: [new OfflineIntegration()],
    });
}

const Initialize = () => {
    const dispatch = useAppDispatch();
    const deviceToken = getDeviceToken();

    const _attemptSignIn = compose(dispatch, attemptSignIn);
    const _checkUserExists = compose(dispatch, checkUserExists);
    const _fetchUser = compose(dispatch, fetchUser);
    const _fetchStores = compose(dispatch, fetchStores);
    const _fetchPurchases = compose(dispatch, fetchPurchases);
    const _fetchMenu = compose(dispatch, fetchStoreMenu);

    const _updateScreenStatus = compose(dispatch, setScreenStatus);

    const user = useSelector(getUser);
    const userExists = useSelector(getUserExists);
    const stores = useSelector(getStores);
    const deviceId = useSelector(getDeviceId);
    const activeScreen = useAppSelector(getActiveScreen);
    const activeStore = useAppSelector(getActiveStore);

    const [isCheckingIfUserExists, setIsCheckingIfUsersExists] = useState(true);
    const [isPingingScreen, setIsPingingScreen] = useState(false);

    const MAX_RETRY_AMOUNT = 8640; // 60 minutes * 12 hours = 720 minutes, multiplied by 12 (once every 5 seconds) to get 8640
    const RETRY_INTERVAL = 5000; // 5 seconds

    // Interval to poll for checking if a user (device) exists that runs until a user is found,
    // or max number of retries is reached
    const { retriesRemaining } = useInterval(
        () => {
            _checkUserExists(
                getDeviceInfo().deviceId // deviceId from redux is not available yet
            );
            _updateScreenStatus(ScreenStatus.REGISTERING);
        },
        isCheckingIfUserExists ? RETRY_INTERVAL : null,
        MAX_RETRY_AMOUNT
    );

    // if _checkUserExists still returns no user after RETRY_INTERVAL * MAX_SIGN_IN_RETRY_AMOUNT (12 hours),
    // set a screen status and show some UI to restart the process manually, so we don't have an
    // infinitely unending interval, but allow someone to retrigger it after the 12 hours
    useEffect(() => {
        if (retriesRemaining === 0) {
            _updateScreenStatus(ScreenStatus.FAILED_TO_SIGN_IN);
        }
    }, [retriesRemaining, _updateScreenStatus]);

    // Turn off the poll and sign in once a user (device) exists
    useEffect(() => {
        if (deviceToken || userExists) {
            setIsCheckingIfUsersExists(false);
            _attemptSignIn();
        }
    }, [deviceToken, userExists]);

    // Interval for pinging activeScreen once a minute
    useInterval(
        () => {
            if (activeScreen) {
                api.screens.ping(activeScreen.id);
            }
        },
        isPingingScreen ? 60000 : null
    );

    // Use onAuthStateChanged to listen for firebaseUser change, and fetchUser once it exists
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
            if (deviceToken || firebaseUser) {
                _fetchUser({ deviceToken, firebaseUser });
            }
        });
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = onIdTokenChanged(
            auth,
            async (user: FirebaseUser) => {
                if (deviceToken) return;

                if (user) {
                    await setAuthHeader(user);
                } else {
                    removeAxiosHeaders();
                }
            }
        );
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (user) {
            _fetchStores(user);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        if (stores && stores.length > 0) {
            const store = stores[0];

            dispatch(
                getScreenByDeviceId({
                    storeId: store._id,
                    deviceId: getDeviceInfo().deviceId, // deviceId from redux is not available yet
                })
            )
                .unwrap()
                .then(screen => {
                    if (screen) {
                        // Screen was found, start the interval polling for pinging
                        setIsPingingScreen(true);
                        // Turn off registration mode, screen is found and registered now
                        _updateScreenStatus(ScreenStatus.REGISTERED);
                    }
                });

            _fetchPurchases({
                storeId: store._id,
                since: moment().clone().subtract(1.5, 'hours').toString(),
                count: 500,
            });

            Sentry.setTag('store', store?.name);
            Sentry.setTag('deviceId', deviceId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stores?.[0]._id, deviceId]);

    useEffect(() => {
        if (activeStore) {
            _fetchMenu(activeStore._id);
        }
    }, [activeStore]);

    return null;
};

export default Initialize;
